import { getApi } from 'api';
import { AxiosResponse } from 'axios';
import IMfaStatus from 'types/auth/IMfaStatus';

/**
 * Clears a user's MFA override, moving them back to "no override".
 *
 * There is no payload value that does this - sending null (or omitting the field) on any of the
 * user/manager/employee save endpoints is a no-op by design, so that a client which does not render
 * the field cannot silently wipe the setting on every save. SuperAdmin only; 400 if the user id
 * does not exist.
 */
export const deleteMfaOverrideRequest = async (userId: number): Promise<boolean> => {
  const response: AxiosResponse<boolean> = await getApi().delete(`UserMfa/${userId}/Override`);
  return response.data;
};

/**
 * Reads whether a user is enrolled in MFA, and how many live devices a reset would sign out.
 * SuperAdmin only. Used to decide whether the reset action is offered at all.
 */
export const getMfaStatusRequest = async (userId: number): Promise<IMfaStatus> => {
  const response: AxiosResponse<IMfaStatus> = await getApi().get(`UserMfa/${userId}/Status`);
  return response.data;
};

/**
 * Resets a user's MFA enrollment: deletes the confirmed enrollment and any abandoned pending one,
 * revokes the user's live mobile devices, and writes one EnrollmentReset audit row naming actor,
 * target, IP and timestamp. Backup codes and open challenges cascade away with the enrollment.
 *
 * The whole thing commits once, so a failure leaves the existing enrollment intact - callers can
 * surface the error without having to reconcile a half-applied reset. One endpoint serves the
 * employee, manager and superadmin modals alike. SuperAdmin only.
 */
export const resetMfaEnrollmentRequest = async (userId: number): Promise<void> => {
  await getApi().post(`UserMfa/${userId}/Reset`);
};

export default deleteMfaOverrideRequest;
